import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { useEventContext } from "Bands/contexts";
import { clearDataByKey, dataMetaSelector } from "Bands/state";
import {
  createEventDetailsExport,
  getEventDetailsExport,
} from "Bands/state/data/actions/eventDetailsExport";
import { eventDetailsExportSelector } from "Bands/state/data/selectors/eventDetailsExport";
import {
  BAND_ACTIONS,
  IEventDetailsExportParams,
  PDFStatus,
} from "Bands/types";
import { useClearActionMetaOnUnMount } from "Bands/utils";
import { getEventBlockPathId } from "Bands/utils/getEventBlockPathId";
import { useDispatch, useSelector } from "react-redux";
import { copy } from "shared/assets";
import { FormBuilder, Modal } from "shared/components";
import { API_ENDPOINTS } from "shared/constants";
import { IAPIState, IDataKeys, IFormButtons, IFormOption } from "shared/types";
import { downloadFile } from "shared/utils";
import { getFields } from "./formData";
import { Download } from "shared/assets/icons";

interface Props {
  setShowModal: (value: boolean) => void;
}

export const EventDetailsExportModal: FC<Props> = ({ setShowModal }: Props) => {
  const dispatch = useDispatch();
  const { event, eventBlocks } = useEventContext();
  const { api, id: createdEventDetailsExportId } = useSelector(
    dataMetaSelector(BAND_ACTIONS.createEventDetailsExport)
  );

  const eDExportSelector = useMemo(() => eventDetailsExportSelector(), []);
  const eventDetailsExport = useSelector(eDExportSelector);

  useClearActionMetaOnUnMount(BAND_ACTIONS.createEventDetailsExport);

  const handleModalClose = useCallback(() => {
    dispatch(clearDataByKey(IDataKeys.eventDetailsExport));
    setShowModal(false);
  }, [dispatch, setShowModal]);

  const eventOptions: IFormOption[] = useMemo(
    () =>
      eventBlocks?.map((block) => ({
        value: getEventBlockPathId({ id: block.id, type: block.type }),
        label: block.title,
      })) || [],
    [eventBlocks]
  );

  const [eventBlockOptions, setEventBlockOptions] = useState(eventOptions);

  const isSubmitting = useMemo(
    () =>
      api?.status === IAPIState.pending ||
      (api?.status === IAPIState.succeeded &&
        eventDetailsExport?.pdfStatus !== PDFStatus.completed),
    [api?.status, eventDetailsExport?.pdfStatus]
  );

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: {
        children: copy.event.pdf.download,
        disabled: isSubmitting,
      },
    }),
    [isSubmitting]
  );

  const onSubmit = (data: IEventDetailsExportParams) => {
    const eventBlockParams = eventBlockOptions
      .filter((option) => data.eventBlocks?.includes(option.value))
      .map((option) => option.value);

    dispatch(
      createEventDetailsExport({
        ...data,
        eventBlocks: eventBlockParams,
        showMyPersonalInfo: !!data.showMyPersonalInfo,
        event: API_ENDPOINTS.EVENT(event?.id!),
      })
    );
  };

  useEffect(() => {
    if (
      api?.status === IAPIState.succeeded &&
      createdEventDetailsExportId &&
      eventDetailsExport?.pdfStatus !== PDFStatus.completed
    ) {
      setTimeout(() => {
        dispatch(getEventDetailsExport(createdEventDetailsExportId));
      }, 2000);
    }

    if (
      api?.status === IAPIState.succeeded &&
      eventDetailsExport?.pdfStatus === PDFStatus.completed
    ) {
      downloadFile(eventDetailsExport.pdfUrl!, eventDetailsExport.title);

      handleModalClose();
    }
  }, [
    api?.status,
    createdEventDetailsExportId,
    eventDetailsExport,
    dispatch,
    handleModalClose,
  ]);

  const defaultValues = useMemo(
    () => ({
      title: event?.title,
      paperSize: "a4",
    }),
    [event?.title]
  );

  return (
    <Modal
      title={copy.event.pdf.create}
      showCloseButton
      icon={Download}
      handleClose={handleModalClose}
    >
      <FormBuilder
        isSubmitting={isSubmitting}
        fields={getFields({ eventBlockOptions, setEventBlockOptions })}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        globalError
        buttons={buttons}
      />
    </Modal>
  );
};
